import arrowDownIcon from "@tabler/icons/outline/arrow-down.svg";
import arrowUpCircleIcon from "@tabler/icons/outline/arrow-up-circle.svg";
import brandAndroidIcon from "@tabler/icons/outline/brand-android.svg";
import headsetIcon from "@tabler/icons/outline/headset.svg";
import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";

import truthArticleImage from "@/assets/images/sideload/truth-article.png";
import truthArticle2xImage from "@/assets/images/sideload/truth-article@2x.png";
import { Button, Card, Icon, Stack } from "@/components/ui";
import { config } from "@/utils/config";

import Header from "./components/header";

enum Versions {
  TEN_OR_ELEVEN = "TEN_OR_ELEVEN",
  TWELVE = "TWELVE",
}

interface InstructionsHeaderProps {
  subheader: React.ReactNode;
  title: React.ReactNode;
  hint: React.ReactNode;
  children?: React.ReactNode;
}

const InstructionsHeader: React.FC<InstructionsHeaderProps> = ({
  subheader,
  title,
  hint,
  children,
}) => {
  return (
    <div>
      <h5 className="font-semibold text-gray-900 dark:text-gray-100">
        {subheader}
      </h5>
      <h2 className="mt-1 text-2xl font-semibold text-primary-500">{title}</h2>

      <p className="my-4 text-gray-900 dark:text-gray-100">{hint}</p>

      {children}
    </div>
  );
};

interface InstructionsGroupProps {
  children: React.ReactNode;
}

const InstructionsGroup: React.FC<InstructionsGroupProps> = ({ children }) => {
  return (
    <div className="-mx-4 grid auto-rows-fr gap-4 lg:grid-cols-3">
      {children}
    </div>
  );
};

interface InstructionStepProps {
  number: number;
  children: React.ReactNode;
}

const InstructionStep: React.FC<InstructionStepProps> = ({ number, children }) => {
  return (
    <Card
      variant="default"
      className="bg-white p-6 text-gray-900 shadow-lg lg:rounded-xl dark:bg-primary-800 dark:text-gray-100 dark:shadow-none"
    >
      <Stack space={2}>
        <h1 className="text-3xl font-bold">{number}</h1>
        <p className="text-lg">{children}</p>
      </Stack>
    </Card>
  );
};

interface InstructionsTopButtonProps {
  to: string;
  children: React.ReactNode;
}

const InstructionsTopButton: React.FC<InstructionsTopButtonProps> = ({
  to,
  children,
}) => {
  return (
    <Link to={to} className="flex items-center space-x-2">
      <Icon src={arrowUpCircleIcon} className="text-primary-500" />
      <span className="font-medium text-primary-500">{children}</span>
    </Link>
  );
};

const SideloadPage = () => {
  const instructionsRef = useRef<HTMLDivElement>(null);
  const [version, setVersion] = useState<Versions>(Versions.TEN_OR_ELEVEN);

  return (
    <div>
      <div className="bg-linear-to-tr from-sea-blue/10 to-primary-600/10 dark:from-primary-900/50 dark:to-primary-800/50">
        <Header />

        <div className="mx-auto max-w-6xl px-4 py-16 sm:px-6 xl:px-0">
          <Stack space={10}>
            <Stack space={4}>
              <h1 className="text-4xl text-primary-600 lg:text-5xl dark:text-primary-500">
                Installing the Sideloading APK
              </h1>

              <p className="text-xl text-gray-900 lg:text-2xl dark:text-gray-200">
                The Truth Social Android App is optimized for devices running
                Android OS 10 or later.
              </p>
            </Stack>

            <div className="mt-6 flex flex-col items-start space-y-4 md:flex-row md:items-center md:space-y-0 md:space-x-4">
              <Button
                size="lg"
                variant="accent"
                icon={arrowDownIcon}
                onClick={() =>
                  instructionsRef.current?.scrollIntoView({
                    behavior: "smooth",
                  })
                }
              >
                Skip to install instructions
              </Button>

              <Button
                size="lg"
                variant="outline"
                icon={brandAndroidIcon}
                href="/truthsocial-release.apk"
                target="_blank"
              >
                Download the APK
              </Button>
            </div>
          </Stack>
        </div>
      </div>

      <section className="mx-auto max-w-6xl px-4 py-16 sm:px-6 xl:px-0">
        <div className="grid grid-cols-1 items-center gap-10 py-6 lg:grid-cols-2">
          <Stack space={4} className="max-w-sm">
            <svg
              viewBox="0 0 16 10"
              width="100"
              className="text-primary-200 dark:text-primary-700"
            >
              <path
                fill="currentColor"
                d="M11.9033 3.21722L13.2306 0.918074C13.3055 0.789182 13.2602 0.625455 13.133 0.550559C13.0059 0.475663 12.8404 0.519206 12.7673 0.648098L11.4226 2.97686C10.3967 2.50832 9.24189 2.24705 8 2.24705C6.75811 2.24705 5.60331 2.50832 4.5774 2.97686L3.23275 0.648098C3.15785 0.519206 2.99412 0.475663 2.86523 0.550559C2.73634 0.625455 2.69279 0.789182 2.76769 0.918074L4.09493 3.21722C1.80623 4.45737 0.256041 6.77394 0 9.4859H16C15.744 6.77394 14.1938 4.45737 11.9033 3.21722ZM4.32658 7.239C3.95558 7.239 3.656 6.93767 3.656 6.56841C3.656 6.19741 3.95733 5.89783 4.32658 5.89783C4.69758 5.89783 4.99717 6.19916 4.99717 6.56841C4.99892 6.93767 4.69758 7.239 4.32658 7.239ZM11.6717 7.239C11.3007 7.239 11.0011 6.93767 11.0011 6.56841C11.0011 6.19741 11.3024 5.89783 11.6717 5.89783C12.0427 5.89783 12.3423 6.19916 12.3423 6.56841C12.344 6.93767 12.0427 7.239 11.6717 7.239Z"
              />
            </svg>

            <h3 className="text-2xl font-medium text-gray-900 dark:text-gray-100">
              Join the conversation on Android devices
            </h3>

            <p className="text-gray-900 dark:text-gray-100">
              Truth Social's sideloaded APK App is now available on select
              Android devices. When downloaded, a sideloaded app will be flagged
              as coming from "unknown sources." Don't worry — this is the
              default prompt for apps obtained outside the Google Play Store.
              Rest assured, the Truth Social APK App is safe to download!
            </p>

            <Stack>
              <p className="font-semibold text-gray-900 dark:text-gray-100">
                Note:
              </p>
              <p className="text-gray-900 dark:text-gray-100">
                Truth Social only supports the version downloaded from this
                specific page.
              </p>
            </Stack>
          </Stack>

          <div className="-mx-4 my-8 sm:-mx-6 xl:mx-0">
            <img
              src={truthArticleImage}
              srcSet={truthArticle2xImage}
              alt="Truth Social post"
              className="w-full overflow-hidden shadow-xl lg:rounded-xl"
            />
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-5xl" ref={instructionsRef}>
        <div className="py-6">
          <div className="mb-12 flex flex-col space-y-4">
            <h1 className="text-center text-lg font-bold text-gray-900 dark:text-gray-100">
              Choose your device
            </h1>

            <div className="flex items-center justify-center space-x-4">
              <Button
                variant={
                  version === Versions.TEN_OR_ELEVEN ? "default" : "outline"
                }
                onClick={() => setVersion(Versions.TEN_OR_ELEVEN)}
              >
                Android 10 & 11
              </Button>
              <Button
                variant={version === Versions.TWELVE ? "default" : "outline"}
                onClick={() => setVersion(Versions.TWELVE)}
              >
                Android 12
              </Button>
            </div>
          </div>

          <Stack className="mb-16 space-y-24">
            {/* Step 1 */}
            <Stack space={4} id="step-one">
              <InstructionsHeader
                subheader={`Android ${version === Versions.TEN_OR_ELEVEN ? "10 & 11" : "12"} Download Instructions`}
                title="Step 1: Turn on Play Protect"
                hint="You'll first need to turn on Play Protect in the Google Play Store before downloading the APK file."
              />

              <InstructionsGroup>
                <InstructionStep number={1}>
                  Open the <strong>Google Play store</strong>.
                </InstructionStep>

                <InstructionStep number={2}>
                  Tap the <strong>menu icon</strong> in the top{" "}
                  {version === Versions.TEN_OR_ELEVEN ? "left" : "right"} of the
                  screen.
                </InstructionStep>

                <InstructionStep number={3}>
                  Tap <strong>Play Protect</strong>.
                </InstructionStep>

                <InstructionStep number={4}>
                  Tap the <strong>gear icon</strong> in the top right of the
                  screen.
                </InstructionStep>

                <InstructionStep number={5}>
                  Make sure <strong>Scan apps with Play Protect</strong> and{" "}
                  <strong>Improve harmful app protection</strong> are both
                  toggled <strong>ON</strong>.
                </InstructionStep>
              </InstructionsGroup>

              <InstructionsTopButton to="#step-one">
                Return to the beginning of Step 1
              </InstructionsTopButton>
            </Stack>

            {/* Step 2 */}
            <Stack space={4} id="step-two">
              <InstructionsHeader
                subheader={`Android ${version === Versions.TEN_OR_ELEVEN ? "10 & 11" : "12"} Download Instructions`}
                title="Step 2: Download to your device"
                hint="Please read the installation instructions below before clicking the Download button."
              >
                <Button
                  className="mb-6"
                  variant="default"
                  block
                  href="/truthsocial-release.apk"
                  target="_blank"
                >
                  Download the APK
                </Button>
              </InstructionsHeader>

              <InstructionsGroup>
                <InstructionStep number={1}>
                  Tap the <strong>Download Truth Social</strong> button to
                  download the file onto your device. If prompted, agree to
                  download the file.
                </InstructionStep>

                <InstructionStep number={2}>
                  View the <strong>browser's download folder</strong> -
                  typically by tapping the stacked dashes or dots in the top
                  right corner.
                </InstructionStep>

                <InstructionStep number={3}>
                  Tap the <strong>truthsocial-release.apk</strong> file.
                </InstructionStep>

                <InstructionStep number={4}>
                  If the browser prompts you for special access, tap{" "}
                  <strong>Settings</strong> and toggle{" "}
                  <strong>special access to ON</strong>.
                </InstructionStep>

                <InstructionStep number={5}>
                  Tap <strong>Install</strong>.
                </InstructionStep>

                <InstructionStep number={6}>
                  Tap <strong>Open</strong> to launch the Truth Social app.
                </InstructionStep>
              </InstructionsGroup>

              <InstructionsTopButton to="#step-two">
                Return to the beginning of Step 2
              </InstructionsTopButton>
            </Stack>
          </Stack>
        </div>
      </section>

      <section className="-mx-4 px-6 py-10 text-center">
        <h2 className="text-3xl font-semibold text-primary-500 dark:text-primary-200">
          Stuck?
        </h2>

        <p className="my-4 text-lg text-gray-900 dark:text-gray-100">
          As always, our support team is standing by for additional questions.
        </p>

        <Button
          variant="outline"
          href={`${config.links.support.href}?subject=Sideload APK Help&body=Your name:%0D%0A%0D%0APhone number:%0D%0A%0D%0AMessage:%0D%0A`}
          icon={headsetIcon}
        >
          Contact support
        </Button>
      </section>
    </div>
  );
};

export default SideloadPage;
